import { useMutation, useQuery } from '@apollo/client';
import { Button, SettingsGroup, SettingsItemTextField } from '@elipssolution/harfang';
import { styled } from '@mui/material';
import { useEffect, useMemo, useState } from 'react';
import { Controller, useForm } from 'react-hook-form';

import SettingsDialogPage from '../../../../../../components/SettingsDialogPage';
import { useSettingsDialog } from '../../../../../../hooks/useSettingsDialog';
import { DomainType } from '../../../../../../types/domain';
import { SUCCESS_ANIMATION_DELAY } from '../../../../../../utils/statusAnimationDelay';
import {
	CREATE_DOMAIN_CHECK_REMITTANCE_SETTING_COUNTERPART_ACCOUNT,
	CreateDomainCheckRemittanceSettingCounterpartAccountType,
	FETCH_DOMAIN_CHECK_REMITTANCE_SETTING_COUNTERPART_ACCOUNT,
	FetchDomainCheckRemittanceSettingCounterpartAccountType,
} from '../../../../api/settingCheckRemittanceCounterpartAccount';
import { AccountType } from '../../../../types/account';

const ActionWrapper = styled('div')({
	display: 'flex',
	flexDirection: 'row-reverse',
	justifyContent: 'space-between',
});

const ErrorWrapper = styled('div')(({ theme: { palette, shape } }) => ({
	height: 36,
	width: '50%',

	display: 'grid',
	placeItems: 'center',

	color: palette.error.main,
	backgroundColor: `${palette.error.main}1A`,
	borderRadius: shape.borderRadius,
}));

type FormFieldType = {
	accountCode: AccountType['code'];
};

type CheckRemittanceCounterpartProps = {
	domainId?: DomainType['id'];
};

const CheckRemittanceCounterpartAccount = ({ domainId }: CheckRemittanceCounterpartProps) => {
	const { onHasDirtyFormChange } = useSettingsDialog();

	const {
		control,
		formState: { isDirty, isValid },
		handleSubmit,
		setValue,
		reset,
	} = useForm<FormFieldType>();

	const isFormValid = useMemo(() => isDirty && isValid, [isDirty, isValid]);

	const [error, setError] = useState<string>();
	const [isCreateCheckRemittanceCounterpartAccountSucceeded, setIsCreateCheckRemittanceCounterpartAccountSucceeded] =
		useState(false);

	useQuery<FetchDomainCheckRemittanceSettingCounterpartAccountType>(
		FETCH_DOMAIN_CHECK_REMITTANCE_SETTING_COUNTERPART_ACCOUNT,
		{
			variables: {
				id: domainId,
			},
			onCompleted: ({ quickentry_settingDomainCheckRemittanceCounterpartAccount: { accountCode } }) => {
				if (accountCode) {
					setValue('accountCode', accountCode, { shouldDirty: true });
				}
			},
		},
	);

	const [createCheckRemittanceCounterpartAccount] =
		useMutation<CreateDomainCheckRemittanceSettingCounterpartAccountType>(
			CREATE_DOMAIN_CHECK_REMITTANCE_SETTING_COUNTERPART_ACCOUNT,
			{
				onCompleted: ({ quickentry_createSettingDomainCheckRemittanceCounterpartAccount: { accountCode } }) => {
					reset({ accountCode });
					setIsCreateCheckRemittanceCounterpartAccountSucceeded(true);
					setTimeout(() => setIsCreateCheckRemittanceCounterpartAccountSucceeded(false), SUCCESS_ANIMATION_DELAY);
				},
				onError: (mutationError) => {
					setError(mutationError.message);
				},
			},
		);

	const onSubmit = ({ accountCode }: FormFieldType) =>
		createCheckRemittanceCounterpartAccount({
			variables: {
				createSettingDomainCheckRemittanceCounterpartAccountInput: {
					accountCode,
					domainId,
				},
			},
		});

	useEffect(() => {
		onHasDirtyFormChange(isDirty);
	}, [isDirty, onHasDirtyFormChange]);

	return (
		<SettingsDialogPage title="Contrepartie de remise de chèques">
			<SettingsGroup>
				<Controller
					name="accountCode"
					control={control}
					rules={{
						pattern: {
							value: /^5/,
							message: 'Le code du compte est incorrect',
						},
						required: true,
					}}
					render={({ field }) => (
						<SettingsItemTextField
							{...field}
							label="Compte de contrepartie"
							description="Le code doit correspondre à un compte de classe 5."
							required
						/>
					)}
				/>
			</SettingsGroup>

			<ActionWrapper>
				<Button disabled={!isFormValid} onClick={handleSubmit(onSubmit)} variant="contained">
					{isCreateCheckRemittanceCounterpartAccountSucceeded ? 'Contrepartie validée' : 'Valider'}
				</Button>

				{error && <ErrorWrapper>{error}</ErrorWrapper>}
			</ActionWrapper>
		</SettingsDialogPage>
	);
};

export default CheckRemittanceCounterpartAccount;
